import { Injectable } from '@angular/core';
import { ECommerceProductWizardMockupService } from '@api-ui-app/src/app/e-commerce-stores/services/e-commerce-product-wizard/e-commerce-product-wizard-mockup.service';
import { PreviewsArchiveService } from '@api-ui-app/src/app/previews-archive/+state/previews-archive.service';
import { SubscriptionsFacade } from '@api-ui-app/src/app/subscriptions/+state/subscriptions.facade';
import { getIsFeatureUsed } from '@api-ui-app/src/app/subscriptions/lib/helpers/visited-apps-local-storage-manager';
import { PremiumIconCrownedFeatures } from '@api-ui-app/src/app/subscriptions/types/icon-crowned-features.enum';
import { EProductVariant } from '@gelato-api-ui/core/e-commerce/e-product-variant';
import { PreflightProductPreviewRequest } from '@gelato-api-ui/core/preflight/preflight-product-preview-request';
import { PreflightApiService } from '@gelato-api-ui/core/preflight/services/preflight-api.service';
import { SanityProductCategoryName } from '@gelato-api-ui/core/sanity/constants/sanity-categories/sanity-product-category-name.enum';
import { StoreProductMediaGraphQlService } from '@product-catalogue/src/lib/store-product-media/services/store-product-media-graph-ql.service';
import { GenerateMockupRequest } from '@product-catalogue/src/lib/store-product-media/types/store-product-media';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class StoreProductMediaService {
  isGelatoPlusOrGoldActive$ = this.subscriptionsFacade.isPaidSubscriptionActive$;
  premiumIconPath$ = this.isGelatoPlusOrGoldActive$.pipe(map(plusActive => this.getPremiumIconPath(plusActive)));
  isSubscriptionsUpdating$ = this.subscriptionsFacade.activePlansIsUpdating$;

  constructor(
    private readonly mediaGraphQlService: StoreProductMediaGraphQlService,
    private readonly subscriptionsFacade: SubscriptionsFacade,
    private readonly wizardMockupService: ECommerceProductWizardMockupService,
    private readonly preflightApiService: PreflightApiService,
    private readonly previewsArchiveService: PreviewsArchiveService,
  ) {}

  getDynamicMockupPreviewUrl(previewRequest: PreflightProductPreviewRequest) {
    const request: GenerateMockupRequest = {
      width: previewRequest.width,
      height: previewRequest.height,
      format: previewRequest.format,
      image_url: previewRequest.image_url,
      product_uid: previewRequest.product_uid,
      scene_id: previewRequest.scene,
      scene_name: previewRequest.scene_name,
      force_dimensions: previewRequest.force_dimensions,
    };

    return this.mediaGraphQlService.fetchMockupPreviewUrl(request);
  }

  getStaticMockupPreviewUrl(previewRequest: PreflightProductPreviewRequest) {
    const request: GenerateMockupRequest = {
      width: previewRequest.width,
      height: previewRequest.height,
      format: previewRequest.format,
      image_url: previewRequest.image_url,
      product_uid: previewRequest.product_uid,
      scene_id: previewRequest.scene,
    };

    return this.mediaGraphQlService.fetchMockupPreviewUrl(request);
  }

  getProductPreviewUrl(previewRequest: PreflightProductPreviewRequest, isStaticScene: boolean) {
    return this.wizardMockupService.isDynamicMockupsEnabled$.pipe(
      switchMap(enabled =>
        enabled && !isStaticScene
          ? this.getDynamicMockupPreviewUrl(previewRequest)
          : of(this.preflightApiService.getProductPreviewUrlDeprecated(previewRequest)),
      ),
    );
  }

  getPremiumIconPath(plusOrGoldActive: boolean): string {
    return this.isPremiumMockupsUsed && plusOrGoldActive
      ? '/assets/subscriptions/circle-crown-silver.svg'
      : '/assets/subscriptions/violet-crown.svg';
  }

  get isPremiumMockupsUsed() {
    return getIsFeatureUsed(PremiumIconCrownedFeatures.PREMIUM_MOCKUPS);
  }

  requestDownloadPreviews(
    sanityProductCategoryName: SanityProductCategoryName,
    productVariants: EProductVariant[],
    previewScenes: string[],
    selectedPreviewFileType: string,
  ) {
    return this.wizardMockupService.isDynamicMockupsEnabled$.pipe(
      switchMap(enabled =>
        enabled
          ? this.previewsArchiveService.downloadMockups(
              sanityProductCategoryName,
              productVariants,
              previewScenes.filter(scene => !!scene),
              selectedPreviewFileType,
            )
          : this.previewsArchiveService.requestDownloadPreviews(
              sanityProductCategoryName,
              productVariants,
              previewScenes,
              selectedPreviewFileType,
            ),
      ),
    );
  }
}
